import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDataChannel, useLocalParticipant } from "@livekit/components-react";

import {
    DEFAULT_GONG_PLAN,
    GONG_MIN_INTERVAL_MS,
    GONG_PLAN_STORAGE_KEY,
    GONG_TOPIC,
    type GongMessage,
    type GongPlan,
    gongIsDue,
    gongOffsets,
    mayRingGong,
    nextGongAt,
    normalizeGongPlan,
} from "../domain/meetingGong";
import {
    type GongPlayer,
    createGongPlayer,
} from "../infrastructure/audio/gongSound";
import { useRememberedChoice } from "./rememberedChoice";

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export type GongChannel = {
    /** Whether this side is allowed to sound one at all. */
    canRing: boolean;
    /** How the next gong is set up. Remembered per browser. */
    plan: GongPlan;
    setPlan: (plan: GongPlan) => void;
    /** True while a strike is sounding, here or on the other side. */
    ringing: boolean;
    /** When the next strike lands, or null when nothing is pending. */
    nextAt: number | null;
    /** True when the last gong did not reach the other person. */
    transportFailed: boolean;
    ring: () => void;
    /**
     * One strike, now, whatever the therapist's plan says.
     *
     * For a bowl a script asked for: `[gong]` marks a moment in a meditation,
     * and starting the timed plan there would leave its closing strikes to
     * arrive minutes later, after the meditation had been stopped.
     */
    ringOnce: () => void;
    /** Call off the strikes still to come, here and everywhere. */
    stop: () => void;
};

/** A remembered plan is input like any other; it is validated, not trusted. */
function decodePlan(stored: string): GongPlan {
    try {
        return normalizeGongPlan(JSON.parse(stored)) ?? DEFAULT_GONG_PLAN;
    } catch {
        return DEFAULT_GONG_PLAN;
    }
}

function isStoredPlan(value: unknown): value is string {
    return typeof value === "string" && value !== "";
}

/**
 * One sound, in every browser in the room — once, or spread over a session.
 *
 * The therapist presses once; a packet goes out on the room's existing data
 * channel and each side sounds its own copy. Both ends play — including the
 * one that pressed — so what the therapist hears is what the patient hears,
 * and a gong that failed to leave the browser is not silently believed to
 * have worked.
 *
 * A sequence sends its *plan*, not one packet per strike, and every browser
 * then schedules the strikes off its own clock. A packet lost twelve minutes
 * in would otherwise be a gong that never sounded for the patient, and a
 * therapist whose laptop slept would take the rest of the sequence with them.
 * The cost is that the two sides drift by however long the packet took, which
 * for a marker of silence is nothing.
 *
 * Call this above whatever draws the control. The player owns an audio
 * context and the sequence owns timers, and remounting it mid-session would
 * build a new context and lose the strikes still to come.
 */
export function useGongChannel(): GongChannel {
    const { localParticipant } = useLocalParticipant();
    const localIdentity = localParticipant?.identity ?? "";

    const [ringing, setRinging] = useState(false);
    const [nextAt, setNextAt] = useState<number | null>(null);
    const [transportFailed, setTransportFailed] = useState(false);

    const [storedPlan, setStoredPlan] = useRememberedChoice<string>(
        GONG_PLAN_STORAGE_KEY,
        isStoredPlan,
        JSON.stringify(DEFAULT_GONG_PLAN)
    );
    const plan = useMemo(() => decodePlan(storedPlan), [storedPlan]);
    const setPlan = useCallback(
        (next: GongPlan) => setStoredPlan(JSON.stringify(next)),
        [setStoredPlan]
    );

    const player = useRef<GongPlayer | null>(null);
    const lastRungAt = useRef<number | null>(null);
    const quietAgain = useRef<ReturnType<typeof setTimeout> | null>(null);
    /** The strikes still to come. Cleared by `cancel`, and on the way out. */
    const pending = useRef<ReturnType<typeof setTimeout>[]>([]);

    useEffect(() => {
        player.current = createGongPlayer();
        return () => {
            if (quietAgain.current) clearTimeout(quietAgain.current);
            for (const timer of pending.current) clearTimeout(timer);
            pending.current = [];
            player.current?.close();
            player.current = null;
        };
    }, []);

    /**
     * Sound one, unless the room is still ringing.
     *
     * The cooldown is checked on the way *in* as well as on the way out, so a
     * client that ignores its own — or a button held down — cannot stack
     * gongs on top of each other in anyone else's room.
     */
    const play = useCallback(() => {
        const now = Date.now();
        if (!gongIsDue(lastRungAt.current, now)) return false;
        lastRungAt.current = now;

        player.current?.ring();
        setRinging(true);
        if (quietAgain.current) clearTimeout(quietAgain.current);
        quietAgain.current = setTimeout(
            () => setRinging(false),
            GONG_MIN_INTERVAL_MS
        );
        return true;
    }, []);

    const cancel = useCallback(() => {
        for (const timer of pending.current) clearTimeout(timer);
        pending.current = [];
        setNextAt(null);
    }, []);

    /**
     * Sound the first strike and book the rest.
     *
     * One timer per remaining strike, each measured from the moment the
     * sequence started rather than from the strike before it, so an hour of
     * scheduling error does not accumulate across a long span. Browsers
     * throttle timers in hidden tabs, but not in a tab holding a live call —
     * which is the only place this ever runs.
     */
    const start = useCallback(
        (sequence: GongPlan) => {
            if (!gongIsDue(lastRungAt.current, Date.now())) return false;

            cancel();
            const startedAt = Date.now();
            play();

            for (const offset of gongOffsets(sequence).slice(1)) {
                pending.current.push(
                    setTimeout(() => {
                        play();
                        setNextAt(nextGongAt(sequence, startedAt, startedAt + offset));
                    }, offset)
                );
            }

            setNextAt(nextGongAt(sequence, startedAt, startedAt));
            return true;
        },
        [cancel, play]
    );

    const onMessage = useCallback(
        (raw: { payload: Uint8Array; from?: { identity: string } }) => {
            if (!mayRingGong(raw.from?.identity ?? "")) return;

            let message: GongMessage;
            try {
                message = JSON.parse(decoder.decode(raw.payload)) as GongMessage;
            } catch {
                return;
            }

            if (message.kind === "gong-stop") {
                cancel();
                return;
            }
            if (message.kind !== "gong") return;

            start(normalizeGongPlan(message) ?? DEFAULT_GONG_PLAN);
        },
        [cancel, start]
    );

    const { send } = useDataChannel(GONG_TOPIC, onMessage);

    const publish = useCallback(
        (message: GongMessage) => {
            setTransportFailed(false);
            try {
                const result = send(encoder.encode(JSON.stringify(message)), {
                    reliable: true,
                    topic: GONG_TOPIC,
                }) as unknown as Promise<void> | void;
                if (result && typeof result.then === "function") {
                    result.then(
                        () => setTransportFailed(false),
                        () => setTransportFailed(true)
                    );
                }
            } catch {
                setTransportFailed(true);
            }
        },
        [send]
    );

    const ring = useCallback(() => {
        if (!mayRingGong(localIdentity)) return;
        if (!start(plan)) return;

        publish({ kind: "gong", ...plan });
    }, [localIdentity, plan, publish, start]);

    const ringOnce = useCallback(() => {
        if (!mayRingGong(localIdentity)) return;
        const once: GongPlan = { ...plan, mode: "single", openStrikes: 1 };
        if (!start(once)) return;

        publish({ kind: "gong", ...once });
    }, [localIdentity, plan, publish, start]);

    const stop = useCallback(() => {
        if (!mayRingGong(localIdentity)) return;
        cancel();
        publish({ kind: "gong-stop" });
    }, [cancel, localIdentity, publish]);

    return {
        canRing: mayRingGong(localIdentity),
        plan,
        setPlan,
        ringing,
        nextAt,
        transportFailed,
        ring,
        ringOnce,
        stop,
    };
}
